import { useEffect } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useTripsStore } from '../../stores/useTripsStore';
import { useOperationalMapStore } from '../../stores/useOperationalMapStore';
import { useLinesStore } from '../../stores/useLinesStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useClientsStore } from '../../stores/useClientsStore';
import { useLoadingStore } from '../../stores/useLoadingStore';
import { useDateStore } from '../../stores/useDateStore';
import { useTripStatusAnalysisStore } from '../../stores/analysis/useTripStatusAnalysisStore';
import { useTripAlertsAnalysisStore } from '../../stores/analysis/useTripAlertsAnalysisStore';
import { useTripPositionsAnalysisStore } from '../../stores/analysis/useTripPositionsAnalysisStore';
import { useTripsVehicleConflictsStore } from '../../stores/useTripsVehicleConflictsStore';
import { useTripOccurrencesStore } from '../../stores/useTripOccurrencesStore';
import { useTripVehiclesChangesStore } from '../../stores/useTripVehiclesChangesStore';
import { parseTripUid } from '../../utils/tripAnalysis';

import {
    loadTrips,
    loadTripAnalysis,
    finishTripsRequest,
    clearTripsRequests,
    isTripsRequestActive
} from '../../services/tripsService';

const tripsDataByRequest = new Map();
const pendingTripEvents = [];

function getTripsRequestBuffer(requestId) {

    let buffer =
        tripsDataByRequest.get(
            requestId
        );

    if (!buffer) {

        buffer = {
            trips: [],
            occurrences: [],
            vehicleChanges: [],
            analysis: {
                positions: [],
                status: [],
                alerts: []
            }
        };

        tripsDataByRequest.set(
            requestId,
            buffer
        );

    }

    return buffer;
}

export function useTripsSocket() {

    const socket = useSocketStore(state => state.socket);

    const linhasSelecionadas = useLinesStore(state => state.linhasSelecionadas);

    const sentidosSet = useLinesStore(state => state.sentidosSet);

    const allowPerfis = useAuthStore(state => state.allowPerfis);

    const allowClientes = useClientsStore(state => state.allowClientes);

    const isOperationalMapMode =
        useOperationalMapStore(
            state => state.isOperationalMapMode
        );

    const operationalMapDateRange =
        useOperationalMapStore(
            state => state.operationalMapDateRange
        );

    // =========================================================
    // LISTENERS
    // =========================================================

    useEffect(() => {

        if (!socket) {
            return;
        }

        const {
            setTrips,
            upsertTrip,
            removeTrip
        } = useTripsStore.getState();

        const { 
            setOccurrencesBatch,
            upsertOccurrence,
            removeOccurrence, 
        } = useTripOccurrencesStore.getState();

        const {
            setVehicleChangesBatch,
            upsertVehicleChange
        } = useTripVehiclesChangesStore.getState();

        const {
            setLoadProgress
        } = useLoadingStore.getState();

        const {
            setPositionsBatch,
            setPosition,
            deletePosition
        } = useTripPositionsAnalysisStore.getState();

        const {
            setAlertsBatch,
            setAlert,
            deleteAlert
        } = useTripAlertsAnalysisStore.getState();

        const {
            setStatusBatch,
            setStatus,
            deleteStatus
        } = useTripStatusAnalysisStore.getState();

        const {
            setConflicts
        } = useTripsVehicleConflictsStore.getState();

        // =====================================================
        // APLICA RESULTADO COMPLETO
        // =====================================================

        const applyTripsResult = ({
            trips,
            analysis,
            occurrences,
            vehicleChanges
        }) => {

            setPositionsBatch(analysis?.positions || []);

            setStatusBatch(analysis?.status || []);

            setAlertsBatch(analysis?.alerts || []);

            setTrips(trips || []);

            setOccurrencesBatch(occurrences || []);

            setVehicleChangesBatch(vehicleChanges || []);

        };

        const applyRealtimeEvent = async ({
            type,
            payload
        }) => {

            switch (type) {

                case 'upsert': {

                    const trip = payload;

                    upsertTrip(trip);

                    const date =
                        trip.DATA_REFERENCIA;

                    const codigo =
                        trip.CODIGO_INTEGRACAO;

                    if (!date || !codigo) {
                        break;
                    }

                    const {
                        getPosition
                    } =
                        useTripPositionsAnalysisStore
                            .getState();

                    const {
                        getStatus
                    } =
                        useTripStatusAnalysisStore
                            .getState();

                    const {
                        getAlerts
                    } =
                        useTripAlertsAnalysisStore
                            .getState();

                    const types = [];

                    if (!getPosition(date, codigo)) {
                        types.push('positions');
                    }

                    if (!getStatus(date, codigo)) {
                        types.push('status');
                    }

                    if (!getAlerts(date, codigo)) {
                        types.push('alerts');
                    }

                    if (!types.length) {
                        break;
                    }

                    try {

                        const analysis =
                            await loadTripAnalysis(
                                socket,
                                {
                                    date,
                                    codigo,
                                    types
                                }
                            );

                        if (!analysis) {
                            break;
                        }

                        if (analysis.positions) {
                            setPosition(
                                analysis.positions
                            );
                        }

                        if (analysis.status) {
                            setStatus(
                                analysis.status
                            );
                        }

                        if (analysis.alerts) {
                            setAlert(
                                analysis.alerts
                            );
                        }

                    } catch (err) {

                        console.error(
                            'Erro ao carregar análises após upsert:',
                            err
                        );

                    }

                    break;
                }

                case 'delete': {

                    const tripUid = payload;

                    const {
                        date,
                        codigo
                    } = parseTripUid(tripUid);

                    removeTrip(tripUid);

                    deletePosition(
                        date,
                        codigo
                    );

                    deleteStatus(
                        date,
                        codigo
                    );

                    deleteAlert(
                        date,
                        codigo
                    );

                    break;
                }

                case 'occurrence-upsert': {

                    upsertOccurrence(
                            payload
                        );

                    break;
                }

                case 'vehicle-change': {

                    upsertVehicleChange(payload);

                    break;
                }

                case 'occurrence-delete': {

                    removeOccurrence(
                        payload.tripUid,
                        payload.occurrenceUid
                    );

                    break;
                }

                default:
                    break;
            }

        };

        const flushPendingEvents = async () => {

            if (!pendingTripEvents.length) {
                return;
            }

            const events =
                pendingTripEvents.splice(
                    0,
                    pendingTripEvents.length
                );

            for (const event of events) {

                await applyRealtimeEvent(
                    event
                );

            }

        };

        const handleDataChunk = ({
            requestId,
            type,
            data,
            percentage
        }) => {

            if ( !isTripsRequestActive(requestId)) {
                return;
            }

            if (!data?.length) {
                return;
            }

            const buffer = getTripsRequestBuffer(requestId);

            switch (type) {

                case 'trips':

                    buffer.trips.push(
                        ...data
                    );

                    break;

                case 'occurrences':

                    buffer.occurrences.push(
                        ...data
                    );

                    break;

                case 'vehicleChanges':

                    buffer.vehicleChanges.push(
                        ...data
                    );

                    break;

                case 'positions':

                    buffer.analysis.positions.push(
                        ...data
                    );

                    break;

                case 'status':

                    buffer.analysis.status.push(
                        ...data
                    );

                    break;

                case 'alerts':

                    buffer.analysis.alerts.push(
                        ...data
                    );

                    break;

                default:

                    console.warn(
                        'Tipo de chunk de trips desconhecido:',
                        type
                    );

            }

            setLoadProgress(percentage);

        };
        
        const handleProgress = ({
            requestId,
            date,
            startDate,
            endDate,
            percentage
        }) => {

            if (!isTripsRequestActive(requestId)) {
                return;
            }

            const {
                isOperationalMapMode,
                operationalMapDateRange
            } = useOperationalMapStore.getState();

            // =========================
            // TORRE
            // =========================

            if (date) {

                if (isOperationalMapMode) {
                    return;
                }

                const currentDate =
                    useDateStore
                        .getState()
                        .today;

                if (currentDate !== date) {
                    return;
                }

                setLoadProgress(percentage ?? 0);

                return;
            }

            // =========================
            // MAPA OPERACIONAL
            // =========================

            if (
                !startDate ||
                !endDate ||
                !isOperationalMapMode ||
                !operationalMapDateRange
            ) {
                return;
            }

            const currentStartDate =
                operationalMapDateRange
                    .startDate
                    .format('YYYY-MM-DD');

            const currentEndDate =
                operationalMapDateRange
                    .endDate
                    .format('YYYY-MM-DD');

            if (
                currentStartDate !== startDate ||
                currentEndDate !== endDate
            ) {
                return;
            }

            setLoadProgress(percentage ?? 0);
        };

        // =====================================================
        // SYNC
        // =====================================================

        const handleSync = () => {

            loadTrips(socket);

        };

        // =====================================================
        // ERROR
        // =====================================================

        const handleError = ({
            requestId
        }) => {

            const isActive = isTripsRequestActive(requestId);

            tripsDataByRequest.delete(requestId);

            finishTripsRequest(requestId);

            if (!isActive) {
                return;
            }

            console.error('Erro ao carregar viagens');

            setLoadProgress(100);

        };

        // =====================================================
        // TORRE - DONE
        // =====================================================

        const handleDateDone = async ({
            requestId,
            date
        }) => {

            const isActive = isTripsRequestActive(requestId);

            const {
                isOperationalMapMode
            } = useOperationalMapStore.getState();

            const currentDate =
                useDateStore
                    .getState()
                    .today;

            if (
                !isActive ||
                isOperationalMapMode ||
                currentDate !== date
            ) {

                tripsDataByRequest.delete(requestId);

                finishTripsRequest(requestId);

                return;
            }

            // =========================
            // RECUPERA RESULTADO
            // =========================

            const result =
                tripsDataByRequest.get(
                    requestId
                ) || {
                    trips: [],
                    occurrences: [],
                    vehicleChanges: [],
                    analysis: {
                        positions: [],
                        status: [],
                        alerts: []
                    }
                };

            tripsDataByRequest.delete(requestId);

            // =========================
            // INSTALA RESULTADO
            // =========================

            applyTripsResult(result);

            // =========================
            // FINALIZA
            // =========================

            setLoadProgress(100);

            finishTripsRequest(requestId);

            const {
                loadingTrips
            } = useLoadingStore.getState();

            if (loadingTrips) {
                return;
            }

            await flushPendingEvents();

        };

        // =====================================================
        // MAPA OPERACIONAL - DONE
        // =====================================================

        const handleRangeDone = async ({
            requestId,
            startDate,
            endDate
        }) => {

            const isActive = isTripsRequestActive(requestId);

            const {
                isOperationalMapMode,
                operationalMapDateRange
            } = useOperationalMapStore.getState();

            if (
                !isActive ||
                !isOperationalMapMode ||
                !operationalMapDateRange
            ) {

                tripsDataByRequest.delete(requestId);

                finishTripsRequest(requestId);

                return;
            }

            const currentStartDate =
                operationalMapDateRange
                    .startDate
                    .format('YYYY-MM-DD');

            const currentEndDate =
                operationalMapDateRange
                    .endDate
                    .format('YYYY-MM-DD');

            if (
                currentStartDate !== startDate ||
                currentEndDate !== endDate
            ) {

                tripsDataByRequest.delete(requestId);

                finishTripsRequest(requestId);

                return;
            }

            // =========================
            // RECUPERA RESULTADO
            // =========================

            const result =
                tripsDataByRequest.get(
                    requestId
                ) || {
                    trips: [],
                    occurrences: [],
                    vehicleChanges: [],
                    analysis: {
                        positions: [],
                        status: [],
                        alerts: []
                    }
                };

            tripsDataByRequest.delete(requestId);

            // =========================
            // INSTALA RESULTADO
            // =========================

            applyTripsResult(result);

            // =========================
            // FINALIZA
            // =========================

            setLoadProgress(100);

            finishTripsRequest(requestId);

            const {
                loadingTrips
            } = useLoadingStore.getState();

            if (loadingTrips) {
                return;
            }

            await flushPendingEvents();

        };

        // =====================================================
        // CONFLITOS DE VEÍCULOS
        // =====================================================

        const handleVehicleConflicts = ({
            vehicleConflicts
        }) => {

            setConflicts(vehicleConflicts || {});

        };

        // =====================================================
        // REALTIME
        // =====================================================

        const handleRealtimeEvent = async (
            type,
            payload
        ) => {

            const {
                loadingTrips
            } = useLoadingStore.getState();

            if (loadingTrips) {

                pendingTripEvents.push({
                    type,
                    payload
                });

                return;
            }

            await applyRealtimeEvent({
                type,
                payload
            });

        };

        const handleUpsert = trip => {

            handleRealtimeEvent('upsert', trip);

        };

        const handleDelete = tripUid => {

            handleRealtimeEvent('delete', tripUid);

        };

        const handleOccurrenceUpserted = (
            occurrence
        ) => {

            handleRealtimeEvent(
                'occurrence-upsert',
                occurrence
            );
        };

        const handleVehicleChange = (change) => {

            handleRealtimeEvent(
                'vehicle-change',
                change
            );

        };

        const handleOccurrenceDeleted = (
            payload
        ) => {

            handleRealtimeEvent(
                'occurrence-delete',
                payload
            );
        };

        // =====================================================
        // LISTENERS
        // =====================================================

        socket.on('trips:sync', handleSync);

        socket.on('tripsByDate:progress', handleProgress);

        socket.on('tripsByDate:data-chunk', handleDataChunk);

        socket.on('tripsByDate:done', handleDateDone);

        socket.on('tripsByDate:error', handleError);

        socket.on('tripsByRange:progress', handleProgress);

        socket.on('tripsByRange:data-chunk', handleDataChunk);

        socket.on('tripsByRange:done', handleRangeDone);

        socket.on( 'tripsByRange:error', handleError);

        socket.on('trips:upserted', handleUpsert);

        socket.on('trips:deleted', handleDelete);

        socket.on('trips:vehicle-conflicts', handleVehicleConflicts);

        socket.on('occurrence:upserted', handleOccurrenceUpserted);

        socket.on('trip:vehicle-change', handleVehicleChange);

        socket.on('occurrence:deleted', handleOccurrenceDeleted);

        return () => {

            socket.off('trips:sync', handleSync);

            socket.off('tripsByDate:progress', handleProgress);

            socket.off('tripsByDate:data-chunk', handleDataChunk);

            socket.off('tripsByDate:done', handleDateDone);

            socket.off('tripsByDate:error', handleError);

            socket.off('tripsByRange:progress', handleProgress);

            socket.off('tripsByRange:data-chunk', handleDataChunk);

            socket.off('tripsByRange:done', handleRangeDone);

            socket.off( 'tripsByRange:error', handleError);

            socket.off('trips:upserted', handleUpsert);

            socket.off('trips:deleted', handleDelete);

            socket.off('trips:vehicle-conflicts', handleVehicleConflicts);

            socket.off('occurrence:upserted', handleOccurrenceUpserted);

            socket.off('trip:vehicle-change', handleVehicleChange);

            socket.off('occurrence:deleted', handleOccurrenceDeleted);

            pendingTripEvents.length = 0;

        };

    }, [socket]);

    // =========================================================
    // SOCKET CONNECT / DISCONNECT
    // =========================================================

    useEffect(() => {

        if (!socket) {
            return;
        }

        const handleDisconnect = () => {

            tripsDataByRequest.clear();
            pendingTripEvents.length = 0;
            clearTripsRequests();

        };

        socket.on('disconnect', handleDisconnect);

        return () => {

            socket.off('disconnect', handleDisconnect);

        };

    }, [socket]);

    useEffect(() => {

        const socket =
            useSocketStore.getState().socket;

        const linesInitialized =
            useLinesStore.getState().linesInitialized;

        if (
            !socket?.connected ||
            isOperationalMapMode ||
            !linesInitialized
        ) {
            return;
        }

        loadTrips(socket);

    }, [
        isOperationalMapMode,
        sentidosSet,
        linhasSelecionadas,
        allowPerfis,
        allowClientes
    ]);

    useEffect(() => {

        const socket =
            useSocketStore.getState().socket;

        const linesInitialized =
            useLinesStore.getState().linesInitialized;

        if (
            !socket?.connected ||
            !isOperationalMapMode ||
            !linesInitialized
        ) {
            return;
        }

        loadTrips(socket);

    }, [
        isOperationalMapMode,
        operationalMapDateRange,
        sentidosSet,
        linhasSelecionadas,
        allowPerfis,
        allowClientes
    ]);

}